import { ERROR_CODES, HEADERS, isApiErrorBody, type ErrorCode } from '@growhero/shared';
import type { z } from 'zod';
import { env } from './env';

/**
 * API client for /api/v1 (ARCHITECTURE §6).
 *  - adds `Authorization: Bearer <Firebase ID token>` and `X-Tenant-Id` automatically
 *  - parses `{ error: { code, message, details } }` into a typed `ApiError`
 *  - optionally validates responses with a shared Zod contract
 *  - retries once with a force-refreshed token on 401
 *
 * Feature code normally uses the react-query hooks in `lib/query.ts` instead of calling this directly.
 */

export type ClientErrorCode = ErrorCode | 'network_error' | 'invalid_response';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(opts: {
    status: number;
    code: ClientErrorCode;
    message: string;
    details?: unknown;
    requestId?: string | null;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
    this.requestId = opts.requestId ?? null;
  }

  /** 402s that the UI turns into upsell / top-up / billing prompts. */
  get isPaymentRequired() {
    return (
      this.code === 'feature_not_in_plan' ||
      this.code === 'insufficient_credits' ||
      this.code === 'subscription_inactive'
    );
  }
  get isRetryable() {
    return this.code === 'network_error' || this.code === 'rate_limited' || this.status >= 500;
  }
  /** Field errors from `validation_failed` (shape: { fieldErrors?: Record<string,string[]> } or zod issues). */
  get fieldErrors(): Record<string, string[]> {
    const d = this.details as
      | {
          fieldErrors?: Record<string, string[]>;
          issues?: { path: (string | number)[]; message: string }[];
        }
      | undefined;
    if (d?.fieldErrors) return d.fieldErrors;
    const out: Record<string, string[]> = {};
    for (const i of d?.issues ?? []) (out[i.path.join('.')] ??= []).push(i.message);
    return out;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

type Query = Record<string, string | number | boolean | null | undefined | (string | number)[]>;

export interface RequestOptions<S extends z.ZodType | undefined = undefined> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  /** Zod schema from @growhero/shared to validate the response. */
  schema?: S;
  /** For money- or message-sending calls. */
  idempotencyKey?: string;
  /** Skip X-Tenant-Id (user-level endpoints such as /me, POST /tenants). */
  noTenant?: boolean;
  /** Skip Authorization (public endpoints like /public/r/:token). */
  anonymous?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

type Out<S> = S extends z.ZodType ? z.infer<S> : unknown;

export interface ApiClientConfig {
  baseUrl: string;
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  getTenantId: () => string | null;
  onError?: (err: ApiError) => void;
  fetchImpl?: typeof fetch;
}

const KNOWN = new Set<string>(ERROR_CODES);

export function createApiClient(config: ApiClientConfig) {
  const doFetch = (...a: Parameters<typeof fetch>) => (config.fetchImpl ?? fetch)(...a);

  function buildUrl(path: string, query?: Query) {
    const url = `${config.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return url;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
      else qs.set(k, String(v));
    }
    const s = qs.toString();
    return s ? `${url}?${s}` : url;
  }

  async function request<S extends z.ZodType | undefined = undefined>(
    path: string,
    opts: RequestOptions<S> = {},
  ): Promise<Out<S>> {
    const send = async (forceRefresh: boolean) => {
      const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
      if (opts.body !== undefined && !(opts.body instanceof FormData))
        headers['Content-Type'] = 'application/json';
      if (!opts.anonymous) {
        const token = await config.getToken(forceRefresh);
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      if (!opts.noTenant && !opts.anonymous) {
        const tid = config.getTenantId();
        if (tid) headers[HEADERS.tenant] = tid;
      }
      if (opts.idempotencyKey) headers[HEADERS.idempotencyKey] = opts.idempotencyKey;
      return doFetch(buildUrl(path, opts.query), {
        method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
        headers,
        body:
          opts.body === undefined
            ? undefined
            : opts.body instanceof FormData
              ? opts.body
              : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    };

    let res: Response;
    try {
      res = await send(false);
      if (res.status === 401 && !opts.anonymous) res = await send(true);
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      const err = new ApiError({ status: 0, code: 'network_error', message: 'Network error' });
      config.onError?.(err);
      throw err;
    }

    const requestId = res.headers.get(HEADERS.requestId);
    const text = res.status === 204 ? '' : await res.text();
    let json: unknown = undefined;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = undefined;
      }
    }

    if (!res.ok) {
      const body = isApiErrorBody(json) ? json.error : null;
      const code: ClientErrorCode =
        body && KNOWN.has(body.code)
          ? body.code
          : res.status >= 500
            ? 'internal'
            : 'invalid_response';
      const err = new ApiError({
        status: res.status,
        code,
        message: body?.message ?? res.statusText ?? 'Request failed',
        details: body?.details,
        requestId,
      });
      config.onError?.(err);
      throw err;
    }

    if (opts.schema) {
      const parsed = opts.schema.safeParse(json);
      if (!parsed.success) {
        const err = new ApiError({
          status: res.status,
          code: 'invalid_response',
          message: 'Response did not match contract',
          details: parsed.error.issues,
          requestId,
        });
        if (import.meta.env.DEV)
          console.error('[api] contract mismatch', path, parsed.error.issues);
        throw err;
      }
      return parsed.data as Out<S>;
    }
    return json as Out<S>;
  }

  return {
    request,
    get: <S extends z.ZodType | undefined = undefined>(
      path: string,
      opts?: Omit<RequestOptions<S>, 'method' | 'body'>,
    ) => request(path, { ...opts, method: 'GET' }),
    post: <S extends z.ZodType | undefined = undefined>(
      path: string,
      body?: unknown,
      opts?: Omit<RequestOptions<S>, 'method' | 'body'>,
    ) => request(path, { ...opts, method: 'POST', body: body ?? {} }),
    put: <S extends z.ZodType | undefined = undefined>(
      path: string,
      body?: unknown,
      opts?: Omit<RequestOptions<S>, 'method' | 'body'>,
    ) => request(path, { ...opts, method: 'PUT', body: body ?? {} }),
    patch: <S extends z.ZodType | undefined = undefined>(
      path: string,
      body?: unknown,
      opts?: Omit<RequestOptions<S>, 'method' | 'body'>,
    ) => request(path, { ...opts, method: 'PATCH', body: body ?? {} }),
    delete: <S extends z.ZodType | undefined = undefined>(
      path: string,
      opts?: Omit<RequestOptions<S>, 'method' | 'body'>,
    ) => request(path, { ...opts, method: 'DELETE' }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

// ── App-wide singleton, wired by AuthProvider (token) and TenantProvider (tenant id) ──
const wiring: {
  getToken: ApiClientConfig['getToken'];
  tenantId: string | null;
  listeners: Set<(e: ApiError) => void>;
} = {
  getToken: async () => null,
  tenantId: null,
  listeners: new Set(),
};

export const api = createApiClient({
  baseUrl: env.apiBaseUrl,
  getToken: (f) => wiring.getToken(f),
  getTenantId: () => wiring.tenantId,
  onError: (e) => wiring.listeners.forEach((l) => l(e)),
});

export function setApiTokenProvider(fn: ApiClientConfig['getToken']) {
  wiring.getToken = fn;
}
export function setApiTenantId(id: string | null) {
  wiring.tenantId = id;
}
export function getApiTenantId() {
  return wiring.tenantId;
}
/** Subscribe to every API error (used for global 402 handling). Returns an unsubscribe fn. */
export function onApiError(fn: (e: ApiError) => void) {
  wiring.listeners.add(fn);
  return () => {
    wiring.listeners.delete(fn);
  };
}

/** Random idempotency key for a single user intent (generate once per click, reuse on retry). */
export function newIdempotencyKey() {
  return crypto.randomUUID();
}
